import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { useIntl } from "react-intl";
import { useRecoilValue } from "recoil";

import { FormatterService, formatterService } from "../../NumberFormatter";

import { InfoButton } from "../../components/Tooltips";
import {
  METRIC_KEYS,
  METRIC_KEY_IDS,
  NIGHTS_UNIT_KEY,
  SERIES_COLORS,
  TARGET_LABEL_KEYS,
  decimal,
  money,
  percent,
} from "./constants";
import { ForecastMetrics } from "./services";

type MetricKey = "mae" | "wape" | "r2";

const format = (
  formatter: FormatterService,
  key: string,
  value: number,
  isPrice: boolean,
  nights: string
) => {
  if (key === "wape") return percent(formatter, value);
  if (key === "r2") return decimal(formatter, value, 3);
  return isPrice
    ? money(formatter, value, 2)
    : `${decimal(formatter, value, 2)} ${nights}`;
};

/** Lower is better for every metric here except R². */
const isBetter = (key: string, value: number | null, other: number | null) => {
  if (value === null || other === null) return false;
  return key === "r2" ? value > other : Math.abs(value) < Math.abs(other);
};

const MetricRows = ({ metrics }: { metrics: ForecastMetrics }) => {
  const intl = useIntl();
  const formatter = useRecoilValue(formatterService);
  const isPrice = metrics.target === "adr";
  const nights = intl.formatMessage({ id: NIGHTS_UNIT_KEY });
  const rows = [
    {
      label: intl.formatMessage({ id: "ML_FORECAST_LEGEND_ML" }),
      color: SERIES_COLORS.ml,
      values: {
        mae: metrics.mae,
        wape: metrics.wape,
        r2: metrics.r2,
      },
      others: {
        mae: metrics.baseline_mae,
        wape: metrics.baseline_wape,
        r2: metrics.baseline_r2,
      },
    },
    {
      label: intl.formatMessage({ id: "ML_FORECAST_LEGEND_BASELINE" }),
      color: SERIES_COLORS.baseline,
      values: {
        mae: metrics.baseline_mae,
        wape: metrics.baseline_wape,
        r2: metrics.baseline_r2,
      },
      others: {
        mae: metrics.mae,
        wape: metrics.wape,
        r2: metrics.r2,
      },
    },
  ];

  return (
    <>
      <TableRow>
        <TableCell
          colSpan={METRIC_KEYS.length + 1}
          sx={{ fontWeight: 700, borderBottom: "none", pt: 2 }}
        >
          {intl.formatMessage({ id: TARGET_LABEL_KEYS[metrics.target] })}
        </TableCell>
      </TableRow>
      {rows.map((row) => (
        <TableRow key={`${metrics.target}-${row.label}`}>
          <TableCell>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: row.color,
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2">{row.label}</Typography>
            </Box>
          </TableCell>
          {METRIC_KEYS.map((key) => {
            const value = row.values[key as MetricKey];
            const other = row.others[key as MetricKey];
            return (
              <TableCell key={key} align="right">
                <Typography
                  variant="body2"
                  sx={{ fontWeight: isBetter(key, value, other) ? 700 : 400 }}
                >
                  {value === null
                    ? "—"
                    : format(formatter, key, value, isPrice, nights)}
                </Typography>
              </TableCell>
            );
          })}
        </TableRow>
      ))}
    </>
  );
};

const MetricsTable = ({ metrics }: { metrics: ForecastMetrics[] }) => {
  const intl = useIntl();
  return (
    // minWidth 0 like ChartCard: without it the table's intrinsic width would widen its grid
    // column and knock the gutter out of line with the rows above.
    <Paper elevation={1} sx={{ minWidth: 0 }}>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>
                {intl.formatMessage({ id: "ML_FORECAST_METRICS_COL_FORECAST" })}
              </TableCell>
              {METRIC_KEYS.map((key) => {
                const info = METRIC_KEY_IDS[key];
                if (!info) return null;
                return (
                  <TableCell key={key} align="right" sx={{ fontWeight: 700 }}>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {intl.formatMessage({ id: info.label })}
                      <InfoButton
                        title={
                          <Box sx={{ maxWidth: 300 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, mb: 0.5 }}
                            >
                              {intl.formatMessage({ id: info.simple })}
                            </Typography>
                            <Typography variant="caption">
                              {intl.formatMessage({ id: info.detail })}
                            </Typography>
                          </Box>
                        }
                        translateTitle={false}
                      />
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {metrics.map((entry) => (
              <MetricRows key={entry.target} metrics={entry} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default MetricsTable;
